// error-reporting.ts — the ONE home for "a screen just crashed in front of a user".
//
// WHY THIS EXISTS
// ---------------
// Everything this app throws currently dies in the browser console. AppErrorBoundary's
// componentDidCatch does exactly one thing: console.error. That line is on the USER's machine,
// and nobody will ever read it.
//
// The platform-liveness watchdog (mig 491) covers the opposite class — things that stop running
// and report nothing. It scores zero here, because nothing it queries knows a screen crashed.
// Two instruments, two classes; neither substitutes for the other.
//
// ── THE PAYLOAD VOCABULARY IS CLOSED, AND THAT IS THE WHOLE DESIGN ────────────────────────
// There is no `message` field. No `stack`. No `componentStack`. Not by convention — the type has
// nowhere to put them, so sending customer data is a COMPILE ERROR rather than a review miss.
//
// This is a measured hazard, not caution. lib/db-errors.ts:25-32 concatenates message + details +
// hint, and PostgREST puts ROW CONTENT into `details` on a unique violation — a real 23505 against
// the customers index reads `Key (primary_phone)=(+9665xxxxxxx) already exists`. The obvious
// "just log err.message" would have shipped customer phone numbers to a database table.
//
// What travels instead: which capture point, which ROUTE PATTERN, which component, an SQLSTATE or
// DOM error NAME, and which FROZEN gate token matched — the token's identity, never its text.
//
// ── IT MUST NEVER MAKE THINGS WORSE ───────────────────────────────────────────────────────
// An error reporter is called at the worst possible moment. Every guard below exists because the
// failure it prevents is an AMPLIFIER: a report that throws gives the catch handler something new
// to report, and a render-crash loop then emits without bound.
//   * a re-entrancy latch — a report begun while one is in flight is DROPPED, never queued
//   * a per-session cap, and per-fingerprint suppression
//   * a circuit breaker — if the failure looks like the backend being down, reporting stops for
//     the session entirely. When the database is unreachable, the last thing it needs is every
//     open tab writing rows about it.
//   * the reporter's own catch writes to console and can never re-enter
//   * no retry, no backoff, no offline queue — fire and forget
//
// Every suppression is a hole, and the honest framing is: this is a SAMPLING device, not a ledger.
// The row's occurrence_count keeps the shape of a storm even when the rows do not.
//
// ── WHAT IT CANNOT DO ─────────────────────────────────────────────────────────────────────
// A crash BEFORE sign-in has no tenant, so the server has nowhere to put it and returns
// 'no_tenant'. The login and MFA screens are exactly where a crash hurts most, and they are not
// covered. Catching them needs an anonymously-writable endpoint — a security-posture decision for
// the Owner, not something to introduce as a side effect of building this.
//
// And if the sink itself is what is broken, nothing reports that. console.error stays as the
// always-on second channel; there is deliberately no meta-reporter watching the reporter.

// NOTE: there is deliberately NO static import of lib/supabase here. main.tsx's whole structure
// exists to keep supabase.ts out of the ?dev preview path — it throws at module scope when the
// env vars are absent, which would white-screen the preview before React paints. This module is
// imported by main.tsx, so it reaches supabase through a DYNAMIC import inside the sink instead.

/** Where the error was caught. Mirrors the client_error_source enum in mig 493. */
export type LxErrorSource =
  | 'render_boundary'
  | 'window_error'
  | 'unhandled_rejection'
  | 'chunk_load'
  | 'rpc';

/**
 * Which FROZEN server gate refused, by identity. Mirrors client_error_gate_token (mig 493),
 * which mirrors the tokens in lib/db-errors.ts:35-74. The TEXT of a gate message never travels.
 */
export type LxGateToken =
  | 'terminal_locked'
  | 'separation_of_duties'
  | 'reassign_self'
  | 'reassign_own_role'
  | 'reassign_prior_decider'
  | 'fls_write_denied';

/**
 * The complete set of things that may be reported.
 *
 * Adding a free-text field here is the one change that would undo this module's guarantee. If a
 * future diagnosis genuinely needs more, add another CLOSED vocabulary (an enum, a token), never
 * a string carrying whatever the error happened to contain.
 */
// The `| undefined` on each optional is required, not noise: the root tsconfig sets
// exactOptionalPropertyTypes, under which `component?: string` REJECTS an explicit undefined —
// and every capture point below passes exactly that when its sanitiser drops a value. (The app's
// own tsconfig is looser, so omitting it typechecks locally and fails the root gate.)
export interface LxErrorEvent {
  source: LxErrorSource;
  /** The react-router route PATTERN ('/customers/:id') — never location.pathname,
   *  which carries record ids. */
  routeKey: string;
  component?: string | undefined;
  /** An SQLSTATE ('42501', '23505') or a DOM error NAME ('TypeError') — never err.message. */
  errorCode?: string | undefined;
  gateToken?: LxGateToken | undefined;
  rpcName?: string | undefined;
  httpStatus?: number | undefined;
}

/** A delivery channel. `deliver` MUST NOT throw; a Sentry adapter would implement this later. */
export interface LxErrorSink {
  deliver(event: LxErrorEvent): void;
}

// ── Guards ────────────────────────────────────────────────────────────────────────────────
const MAX_PER_SESSION = 20;
const SUPPRESS_WINDOW_MS = 5 * 60 * 1000;

let inFlight = false;
let sentThisSession = 0;
let circuitOpen = false;
const lastSeenByFingerprint = new Map<string, number>();

let sink: LxErrorSink | null = null;

/** Swap the delivery channel (tests, or a Sentry adapter later). */
export function registerSink(next: LxErrorSink | null): void {
  sink = next;
}

/** Test-only: return the guards to their initial state. Never called by app code. */
export function __resetErrorReportingForTests(): void {
  inFlight = false;
  sentThisSession = 0;
  circuitOpen = false;
  lastSeenByFingerprint.clear();
}

/**
 * The same identity the server computes, used here only to suppress repeats before they leave
 * the browser. The server recomputes it independently — this one is an optimisation, never the
 * authority, so a client that lies about it can still only inflate its own suppression.
 */
export function fingerprintOf(e: LxErrorEvent): string {
  return [e.source, e.routeKey, e.component ?? '', e.errorCode ?? '',
    e.rpcName ?? '', e.gateToken ?? ''].join('|');
}

/**
 * Does this look like "the backend is unreachable" rather than "this screen has a bug"?
 *
 * When it does, reporting stops for the rest of the session: writing rows about an outage
 * requires the very thing that is down, and every open tab would try at once.
 */
export function looksLikeBackendOutage(e: LxErrorEvent): boolean {
  if (e.httpStatus !== undefined && e.httpStatus >= 500) return true;
  // The DOM name for a failed fetch. Not the message — the NAME.
  return e.errorCode === 'TypeError' && (e.source === 'rpc' || e.source === 'unhandled_rejection');
}

/**
 * Pure decision function: should this event be delivered? Exported so the unit tests can assert
 * every suppression path without touching the network or a clock.
 */
export function shouldDeliver(
  e: LxErrorEvent,
  now: number,
): { deliver: boolean; reason: string } {
  if (circuitOpen) return { deliver: false, reason: 'circuit_open' };
  if (inFlight) return { deliver: false, reason: 'reentrant' };
  if (sentThisSession >= MAX_PER_SESSION) return { deliver: false, reason: 'session_cap' };

  const fp = fingerprintOf(e);
  const last = lastSeenByFingerprint.get(fp);
  if (last !== undefined && now - last < SUPPRESS_WINDOW_MS) {
    return { deliver: false, reason: 'duplicate_suppressed' };
  }
  return { deliver: true, reason: 'ok' };
}

/**
 * THE ONLY FUNCTION CALL SITES IMPORT.
 *
 * Fire-and-forget and total: it returns void, never throws, and never rejects. A caller is
 * already handling a failure — it must not have to handle this one too.
 */
export function reportClientError(e: LxErrorEvent): void {
  try {
    // The always-on second channel. It runs BEFORE any suppression, so a developer watching the
    // console still sees everything even when the sink is silent by design.
    // eslint-disable-next-line no-console
    console.error('[lx-error]', e.source, e.routeKey, e.component ?? '', e.errorCode ?? '');

    const now = Date.now();
    const verdict = shouldDeliver(e, now);
    if (!verdict.deliver) return;

    if (looksLikeBackendOutage(e)) {
      // Trip the breaker and do not deliver THIS one either — the report would need the same
      // unreachable backend.
      circuitOpen = true;
      return;
    }

    lastSeenByFingerprint.set(fingerprintOf(e), now);
    sentThisSession += 1;
    inFlight = true;

    const channel: LxErrorSink = sink ?? defaultSink;
    try {
      channel.deliver(e);
    } finally {
      // Released synchronously: the latch exists to stop a re-entrant crash loop within one tick,
      // not to serialise network calls. Holding it until a promise settles would drop every
      // unrelated error that happened to arrive during an in-flight request.
      inFlight = false;
    }
  } catch {
    // The reporter's own failure ends here. It must never re-enter the sink.
    inFlight = false;
  }
}

/**
 * Build the RPC arguments — field by field, never by spreading the event.
 *
 * THIS FUNCTION IS THE RUNTIME HALF OF THE CLOSED-VOCABULARY GUARANTEE. The TypeScript interface
 * stops a `message` field at compile time, but a caller that widens with `as LxErrorEvent`, or a
 * plain-JS caller, could still hand over an object carrying anything. Because the parameters are
 * enumerated here, an extra property has no route to the database — and the unit test proves it
 * by passing deliberate junk and asserting the key set.
 */
export function toRpcParams(e: LxErrorEvent): Record<string, string | number | null> {
  return {
    p_source: e.source,
    p_route_key: e.routeKey,
    p_component: e.component ?? null,
    p_error_code: e.errorCode ?? null,
    p_gate_token: e.gateToken ?? null,
    p_rpc_name: e.rpcName ?? null,
    p_http_status: e.httpStatus ?? null,
    p_app_version: null,
  };
}

/** Default channel: the mig-493 RPC. Returns a status string and never raises server-side. */
const defaultSink: LxErrorSink = {
  deliver(e: LxErrorEvent): void {
    // Dynamic import: see the note at the top — a static one would drag supabase.ts into the
    // ?dev preview bundle. Both the import AND the call get a rejection handler, because an
    // unhandled rejection here would come straight back through the global handler below.
    void import('@/lib/supabase')
      .then(({ supabase }) => supabase.rpc('report_client_error', toRpcParams(e)))
      .then(undefined, () => undefined);
  },
};

// ── Turning a live browser into the closed vocabulary ──────────────────────────────────────

/** A JS identifier and nothing else — a component or error NAME is code, never user data. */
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/;

/** Keep a value only if it is a bare identifier. Anything else is dropped, not truncated. */
export function identifierOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && IDENTIFIER.test(value) ? value : undefined;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_SEGMENT = /^[A-Za-z0-9._-]{1,40}$/;

/**
 * Turn a live pathname into the ROUTE PATTERN the router declares ('/customers/:id').
 *
 * The boundary sits ABOVE the router (main.tsx), so there is no useMatch to ask — and a
 * populated pathname must never be sent: a record id is tenant-confidential even when it is not
 * personal data. So the masking is done here, and it defaults to masking: a segment is kept only
 * if it looks like a route literal, and ANYTHING else — a uuid, a number, a percent-encoded
 * Arabic name, an email — becomes ':id'. Wrong-but-masked beats right-but-leaking.
 */
export function routeKeyFromPath(pathname: string): string {
  const parts = pathname.split('/').map((seg) => {
    if (seg === '') return '';
    if (UUID.test(seg)) return ':id';
    if (/^\d+$/.test(seg)) return ':id';
    return SAFE_SEGMENT.test(seg) ? seg : ':id';
  });
  return parts.join('/').slice(0, 200) || '/';
}

/** The current route pattern, or 'unknown' outside a browser. */
function currentRouteKey(): string {
  try {
    return routeKeyFromPath(window.location.pathname);
  } catch {
    return 'unknown';
  }
}

/**
 * Install the window-level capture points. Call once from main.tsx, on BOTH entry paths — a
 * crash in the preview bundle is still a crash, and the sink simply returns 'no_tenant' there.
 *
 * Deliberately NOT installed: a handler for 'vite:preloadError'. main.tsx already treats that as
 * recoverable and reloads; reporting it would file a row for every ordinary post-deploy reload.
 * The SECOND occurrence within a minute is the real failure, and that one falls through to the
 * error boundary and gets reported as a render_boundary crash.
 */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    const name = identifierOrUndefined((event.error as Error | undefined)?.name);
    reportClientError({
      source: 'window_error',
      routeKey: currentRouteKey(),
      errorCode: name,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as { name?: unknown; code?: unknown } | undefined;
    // An SQLSTATE ('42501') from a PostgREST error, else the DOM error name. Both are closed
    // vocabularies; `message` is never read.
    const code =
      typeof reason?.code === 'string' && /^[A-Za-z0-9]{1,10}$/.test(reason.code)
        ? reason.code
        : identifierOrUndefined(reason?.name);
    reportClientError({
      source: 'unhandled_rejection',
      routeKey: currentRouteKey(),
      errorCode: code,
    });
  });
}

/**
 * The render-boundary capture. Takes the React ErrorInfo componentStack and pulls ONLY the
 * innermost component name out of it — an identifier, matched strictly, dropped if it does not
 * match. The stack itself never travels: it carries props in some React builds.
 */
export function reportRenderCrash(error: unknown, componentStack: string | null | undefined): void {
  const first = /\s*(?:at|in)\s+([A-Za-z_$][A-Za-z0-9_$]*)/.exec(componentStack ?? '');
  reportClientError({
    source: 'render_boundary',
    routeKey: currentRouteKey(),
    component: identifierOrUndefined(first?.[1]),
    errorCode: identifierOrUndefined((error as Error | undefined)?.name),
  });
}
